<script setup lang="ts">
const { data: partners } = await useAsyncData('brands', () => $larafetch(`/brands`))

const logoClasses = {
  'approach-guides': 'h-10',
  'andbeyond': 'h-10 max-h-10',
  'aurora-expeditions': 'h-10 max-h-10',
  'explora-journeys': 'max-h-14 h-14',
  'hurtigruten': 'h-5',
  'hx': 'h-10 max-h-10'
}

const brandFilter = ref<'all' | 'experiences' | 'websites'>('all')

const isTestimonialsExpanded = ref(false)

const filteredPartners = computed(() => {
  const allPartners = partners.value?.data ?? []

  if (brandFilter.value === 'experiences') {
    return allPartners.filter(partner => partner.cobrandable_content_experiences_count)
  }

  if (brandFilter.value === 'websites') {
    return allPartners.filter(partner => partner.has_cobrandable_website)
  }

  return allPartners
})

const demoPartner = {
  name: 'Blue Horizon Cruises',
  site: { domain: 'www.bluehorizoncruises.com' },
  styles: { primary_color: '#2664A3' }
}

const testimonials = [
  {
    type: 'brand',
    quote:
      'This collaboration with Approach Guides gives advisors the tools to tell our story with accuracy and impact, inspire clients, and convert interest into bookings.',
    name: 'Jeremy McClellan',
    position: 'VP Trade Sales & Support',
    company: 'The Ritz-Carlton Yacht Collection',
    logo: 'https://cdn.approachguides.co/184118/logo-ritz-carlton-yacht-collection-web.jpg'
  },
  {
    type: 'brand',
    quote:
      'Today\'s luxury traveler expects inspiration and immediacy, and our advisors need technology that delivers both. Working with Approach Guides provides our travel partners an elegant, intuitive way to market Crystal, combining beautiful storytelling with powerful lead generation. It\'s a meaningful step forward in how we support advisor success and help them grow their business.',
    name: 'Matias Lira',
    position: 'SVP Trade Sales Americas',
    company: 'Crystal',
    logo: 'https://cdn.approachguides.co/190141/logo-crystal-cruises-space.jpg'
  },
  {
    type: 'brand',
    quote:
      'Approach Guides allows advisors to save time on marketing so they can focus on what they do best: building trusted relationships with their customers.',
    name: 'Janet Bava',
    position: 'Chief Commercial Officer',
    company: 'Windstar',
    logo: 'https://cdn.approachguides.co/154228/Logo-Windstar.webp'
  },
  {
    type: 'brand',
    quote:
      'This important partnership demonstrates our enduring commitment to our success together — we know this technology works, enabling advisors to seamlessly share content that communicates AmaWaterways\' story and drives private leads to elevate their business.',
    name: 'Kristin Karst',
    position: 'Co-founder and Chief Brand Ambassador',
    company: 'AmaWaterways',
    logo: 'https://cdn.approachguides.co/160817/AmaWaterways-Logo-Horizontal-Color.png'
  },
  {
    type: 'brand',
    quote:
      'Our website is one of our most powerful sales tools, and our expanded partnership with Approach Guides will allow travel advisors to harness its power while helping them grow their business. Advisors can now share any page from our website with clients to showcase our industry-leading expeditions while ensuring all leads will come back directly to the advisor.',
    name: 'Kathi Riddle',
    position: 'SVP of Sales',
    company: 'National Geographic-Lindblad Expeditions',
    logo: 'https://cdn.approachguides.co/96356/logo-national-geographic-lindblad.png'
  },
  {
    type: 'brand',
    quote:
      'It\'s unbelievable how easy and quick and immediate this solution is. This is going to change the way travel advisors communicate with their customers. I LOVE this solution. People say often we\'ve made it easy, we\'ve done all the heavy lifting. With this there is no lifting. You\'re literally just clicking a button.',
    name: 'Joe Leon',
    position: 'VP of Sales, Americas',
    company: 'Silversea',
    logo: 'https://cdn.approachguides.co/101175/logo-silversea-new-1000.png'
  },
  {
    type: 'brand',
    quote:
      'Our new technology is poised to revolutionize how our valued travel advisor partners sell this hot category [group sales, river cruises], allowing them to be far more efficient, deliver an elevated class of service to their clients, and earn more.',
    name: 'Steve Spivak',
    position: 'VP of Global Sales and Reservations',
    company: 'Tauck',
    logo: 'https://cdn.approachguides.co/13770/Logo-Tauck.png'
  }
]

useHead({
  title: 'Co-brandable Content Experiences & Websites'
})
</script>

<template>
  <div>
    <Band
      :ui="{ base: 'pb-0 md:pb-0 lg:pb-0' }"
      spaced
    >
      <div
        v-if="false"
        class="relative"
      >
        <img
          src="https://inspires.to/homepage-bg.jpg"
          class="absolute inset-0 h-full w-full rounded-lg object-cover"
        />
        <div class="absolute inset-0 rounded-lg bg-slate-900/50" />
        <div class="relative flex flex-col items-center justify-center space-y-8 p-10 text-center md:p-24">
          <div class="text-white/75">
            <Heading
              type="h1"
              :ui="{ base: 'font-normal text-white' }"
            >
              The brands you sell,<br /><strong class="font-bold text-white">co-branded as you.</strong>
            </Heading>
            <LeadParagraph class="mt-6 max-w-xl font-semibold">
              Co-brand ready-made content experiences and entire supplier websites in seconds. Share them across email,
              social and itineraries — and every lead comes straight to you. Completely free.
            </LeadParagraph>
          </div>
          <LoginOrRegisterForm
            button-label="Get Started"
            class="w-full max-w-md md:rounded-full md:bg-white md:p-1"
            inline
            :ui="{
              input: { base: 'ring-0 bg-white rounded-full', input: 'px-6 text-center md:text-left md:pr-0' },
              button: { base: 'rounded-full' },
              form: { base: 'w-full max-w-md' }
            }"
          />
          <div
            class="-mx-[2px] flex flex-col items-center gap-3 rounded-full p-0.5 pr-5 text-xs md:flex-row md:bg-slate-900/15"
          >
            <div class="flex -space-x-4">
              <img
                src="https://i.pravatar.cc/80?img=1"
                class="size-8 rounded-full border-2 border-white/10"
              />
              <img
                src="https://i.pravatar.cc/80?img=3"
                class="size-8 rounded-full border-2 border-white/10"
              />
              <img
                src="https://i.pravatar.cc/80?img=9"
                class="size-8 rounded-full border-2 border-white/10"
              />
              <img
                src="https://i.pravatar.cc/80?img=14"
                class="size-8 rounded-full border-2 border-white/10"
              />
              <img
                src="https://i.pravatar.cc/80?img=23"
                class="size-8 rounded-full border-2 border-white/10"
              />
              <img
                src="https://i.pravatar.cc/80?img=32"
                class="size-8 rounded-full border-2 border-white/10"
              />
            </div>
            <div class="rounded-full bg-slate-900/10 p-1 px-2.5 font-semibold text-white/75 md:bg-transparent md:p-0">
              Used by <strong class="font-bold text-white/85">80k+ travel advisors</strong>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div>
          <Heading
            type="h2"
            flourish="START MARKETING"
            flourish-color="brand"
          >
            Choose a brand to get started
          </Heading>

          <p class="mt-3 max-w-2xl">
            Instantly co-brand marketing pages and websites from the world's leading travel brands. Share with clients in seconds and generate private leads.
          </p>

          <div class="mt-10 flex select-none flex-wrap items-center gap-3 text-sm font-medium lg:mt-12">
            <button
              type="button"
              class="rounded-full px-4 py-1.5 transition-colors"
              :class="brandFilter === 'all' ? 'bg-primary text-white' : 'bg-slate-100 hover:bg-slate-200'"
              @click="brandFilter = 'all'"
            >
              All
            </button>
            <button
              type="button"
              class="flex items-center space-x-2 rounded-full px-4 py-1.5 transition-colors"
              :class="brandFilter === 'experiences' ? 'bg-primary text-white' : 'bg-slate-100 hover:bg-slate-200'"
              @click="brandFilter = 'experiences'"
            >
              <div class="h-1 w-2 rounded-full bg-slate-400" />
              <div>Marketing Pages</div>
            </button>
            <button
              type="button"
              class="flex items-center space-x-2 rounded-full px-4 py-1.5 transition-colors"
              :class="brandFilter === 'websites' ? 'bg-primary text-white' : 'bg-slate-100 hover:bg-slate-200'"
              @click="brandFilter = 'websites'"
            >
              <div class="h-1 w-2 rounded-full bg-[#E9C177]" />
              <div>Websites</div>
            </button>
          </div>
        </div>

        <div class="mt-8 grid select-none grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          <NuxtLink
            v-for="partner in filteredPartners"
            :key="partner.slug"
            :to="brandFilter === 'websites' ? `/${partner.slug}/website` : `/${partner.slug}/experiences`"
            class="group relative flex h-28 items-center justify-center rounded-lg border p-6 transition-shadow hover:shadow-md"
            :alt="partner.name"
          >
            <div class="flex h-28 items-center justify-center">
              <NuxtImg
                :src="partner.logo.full_url"
                height="40"
                :class="$cn(['max-h-8', logoClasses[partner.slug]])"
              />
              <div class="absolute bottom-2 right-2 flex space-x-0.5">
                <div
                  v-show="partner.cobrandable_content_experiences_count"
                  class="h-1 w-2 rounded-full bg-slate-400/50 group-hover:bg-slate-400"
                />
                <div
                  v-show="partner.has_cobrandable_website"
                  class="h-1 w-2 rounded-full bg-[#E9C177]/50 group-hover:bg-[#E9C177]"
                />
              </div>
            </div>
          </NuxtLink>
        </div>
      </div>
    </Band>

    <Band
      container="full"
      :ui="{ base: 'pb-0 md:pb-0 lg:pb-0 px-0 md:px-0' }"
    >
      <div class="px-8 md:px-10">
        <Container>
          <div class="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <Heading
                type="h2"
                flourish="MAKE THEIR CONTENT YOURS"
                flourish-color="brand"
              >
                Co-brandable Content Experiences
              </Heading>
              <p class="mt-3 max-w-2xl">
                Ready-made, beautifully designed content built around the brands and destinations you sell. Add your
                logo, photo and a personal message — then share it by email, on social, or inside an itinerary. In
                seconds, with every lead direct to you.
              </p>
            </div>
            <p>
              <Button
                to="/experiences"
                variant="link"
                append-icon="long-arrow-right"
              >
                View All
              </Button>
            </p>
          </div>
        </Container>
      </div>
      <ContentExperiencesSlider
        endpoint="content-experiences/new"
        class="mt-10 lg:mt-12"
      />
    </Band>

    <CopilotBand
      :partner="demoPartner"
      class="pb-20 md:pb-24 lg:pb-36"
    >
      <Button
        to="/websites"
        append-icon="long-arrow-right"
        class="self-start"
      >
        Co-brand Websites
      </Button>

      <template #preview>
        <img
          src="/copilot-example.jpg"
          alt=""
          class="w-full rounded-b-[inherit]"
        />
      </template>
    </CopilotBand>

    <Band :ui="{ base: 'pt-0 md:pt-0 lg:pt-0' }">
      <div class="space-y-8">
        <div>
          <div class="flex flex-col items-center justify-center text-center">
            <Heading
              type="h2"
              flourish="TRUSTED ACROSS THE INDUSTRY"
              flourish-color="brand"
              :ui="{ base: 'font-normal' }"
            >
              Travel advisors & brands worldwide<br /><strong class="font-semibold">love what we're doing</strong>
            </Heading>
            <div
              v-if="false"
              class="mt-10 flex select-none flex-wrap items-center gap-3 text-sm font-medium lg:mt-12"
            >
              <button
                type="button"
                class="rounded-full px-4 py-1.5 transition-colors"
                :class="brandFilter === 'all' ? 'bg-primary text-white' : 'bg-slate-100 hover:bg-slate-200'"
                @click="brandFilter = 'all'"
              >
                All
              </button>
              <button
                type="button"
                class="flex items-center space-x-2 rounded-full px-4 py-1.5 transition-colors"
                :class="brandFilter === 'experiences' ? 'bg-primary text-white' : 'bg-slate-100 hover:bg-slate-200'"
                @click="brandFilter = 'experiences'"
              >
                <div>Travel Advisors</div>
              </button>
              <button
                type="button"
                class="flex items-center space-x-2 rounded-full px-4 py-1.5 transition-colors"
                :class="brandFilter === 'websites' ? 'bg-primary text-white' : 'bg-slate-100 hover:bg-slate-200'"
                @click="brandFilter = 'websites'"
              >
                <div>Brands</div>
              </button>
            </div>
          </div>
        </div>

        <!-- Swiped through on phones; full width so the next quote peeks in from the edge -->
        <Slider
          :total="testimonials.length"
          class="-mx-8 sm:hidden"
        >
          <Testimonial
            v-for="testimonial in testimonials"
            :key="testimonial.name"
            :type="testimonial.type"
            :quote="testimonial.quote"
            :name="testimonial.name"
            :position="testimonial.position"
            :avatar="testimonial.avatar"
            :logo="testimonial.logo"
            :company="testimonial.company"
          />
        </Slider>

        <!-- Shows about two rows until expanded, fading out so it's clear there's more; each quote takes the height it needs -->
        <div class="hidden sm:block">
          <div
            id="testimonials"
            class="relative"
            :class="{ 'max-h-[40rem] overflow-hidden': !isTestimonialsExpanded }"
          >
            <div class="gap-6 sm:columns-2 lg:columns-3">
              <Testimonial
                v-for="testimonial in testimonials"
                :key="testimonial.name"
                :type="testimonial.type"
                :quote="testimonial.quote"
                :name="testimonial.name"
                :position="testimonial.position"
                :avatar="testimonial.avatar"
                :logo="testimonial.logo"
                :company="testimonial.company"
                class="mb-6 break-inside-avoid"
              />
            </div>

            <div
              v-if="!isTestimonialsExpanded"
              class="absolute inset-x-0 bottom-0 flex h-48 items-end justify-center bg-gradient-to-t from-white via-white/80 to-transparent pb-2"
            >
              <button
                type="button"
                aria-controls="testimonials"
                :aria-expanded="isTestimonialsExpanded"
                class="flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-heading shadow-md ring-1 ring-black/5 transition-shadow hover:shadow-lg"
                @click="isTestimonialsExpanded = true"
              >
                {{ $t('show_more', 'Show more') }}
                <Icon
                  icon="chevron-down"
                  class="text-xs"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Band>
  </div>
</template>
